import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FaSun, FaMoon, FaDesktop, FaChevronDown } from 'react-icons/fa'
import { useTheme } from '../../../contexts/ThemeContext'
import './ThemeToggle.css'

const ThemeToggle = () => {
  const { isDarkMode, toggleTheme, setAutoTheme, setLightTheme, setDarkTheme, systemPreference } = useTheme()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const getCurrentThemeInfo = () => {
    const savedTheme = localStorage.getItem('theme-preference')
    
    if (savedTheme === 'auto') {
      return {
        icon: <FaDesktop />,
        label: 'Auto',
        description: `Following system (${systemPreference})`
      }
    } else if (isDarkMode) {
      return {
        icon: <FaMoon />,
        label: 'Dark',
        description: 'Dark mode'
      }
    } else {
      return {
        icon: <FaSun />,
        label: 'Light',
        description: 'Light mode'
      }
    }
  }

  const themeOptions = [
    {
      id: 'light',
      icon: <FaSun />,
      label: 'Light',
      description: 'Light mode',
      action: setLightTheme
    },
    {
      id: 'dark',
      icon: <FaMoon />,
      label: 'Dark',
      description: 'Dark mode',
      action: setDarkTheme
    },
    {
      id: 'auto',
      icon: <FaDesktop />,
      label: 'Auto',
      description: 'Follow system preference',
      action: setAutoTheme
    }
  ]

  const currentTheme = getCurrentThemeInfo()

  const handleOptionClick = (option) => {
    option.action()
    setIsDropdownOpen(false)
  }

  return (
    <div className="theme-toggle">
      {/* Simple toggle button for mobile */}
      <button 
        className="theme-toggle-simple"
        onClick={toggleTheme}
        aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
      >
        <motion.div
          initial={false}
          animate={{ rotate: isDarkMode ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          {isDarkMode ? <FaSun /> : <FaMoon />}
        </motion.div>
      </button>

      {/* Advanced dropdown for desktop */}
      <div className="theme-toggle-dropdown">
        <button
          className="theme-toggle-button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          aria-label="Theme options"
        >
          <div className="theme-toggle-current">
            <span className="theme-icon">{currentTheme.icon}</span>
            <span className="theme-label">{currentTheme.label}</span>
            <motion.span 
              className="theme-chevron"
              animate={{ rotate: isDropdownOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <FaChevronDown />
            </motion.span>
          </div>
        </button>

        <AnimatePresence>
          {isDropdownOpen && (
            <motion.div
              className="theme-dropdown-menu"
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              {themeOptions.map((option) => (
                <motion.button
                  key={option.id}
                  className="theme-option"
                  onClick={() => handleOptionClick(option)}
                  whileHover={{ backgroundColor: 'var(--hover-bg)' }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="theme-option-icon">{option.icon}</span>
                  <div className="theme-option-text">
                    <span className="theme-option-label">{option.label}</span>
                    <span className="theme-option-description">{option.description}</span>
                  </div>
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Click outside to close */}
      {isDropdownOpen && (
        <div 
          className="theme-dropdown-overlay"
          onClick={() => setIsDropdownOpen(false)}
        />
      )}
    </div>
  )
}

export default ThemeToggle
